import { FunctionComponent, useState } from "react";
import { 
    Button, 
    Menu, 
    Typography, 
    Box
} from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./DateRangeFilter.module.css";
import moment from "moment";

interface DateRangeFilterProps {
    startDate: Date | null;
    endDate: Date | null;
    onDateChange: (start: Date | null, end: Date | null) => void;
    onClear: () => void;
}

const DateRangeFilter: FunctionComponent<DateRangeFilterProps> = ({
    startDate,
    endDate,
    onDateChange,
    onClear
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [localStart, setLocalStart] = useState<Date | null>(startDate);
    const [localEnd, setLocalEnd] = useState<Date | null>(endDate);
    const open = Boolean(anchorEl);

    // Sync local state when props change or menu opens
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setLocalStart(startDate);
        setLocalEnd(endDate);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleStartDateChange = (date: Date | null) => {
        setLocalStart(date);
        if (date && localEnd) {
            onDateChange(date, localEnd);
        } else if (!date) {
            // If clearing, we should probably clear the global state too
            onDateChange(null, null);
        }
    };

    const handleEndDateChange = (date: Date | null) => {
        setLocalEnd(date);
        if (localStart && date) {
            onDateChange(localStart, date);
        } else if (!date) {
            // If clearing, we should probably clear the global state too
            onDateChange(null, null);
        }
    };

    const isActive = startDate || endDate;

    return (
        <div>
            <Button
                variant="outlined"
                onClick={handleClick}
                className={`${styles.filterButton} ${isActive ? styles.activeButton : ""}`}
                startIcon={<CalendarTodayIcon />}
                disableRipple
            >
                <span className={styles.buttonLabel}>Date Range</span>
                {isActive && (
                    <span className={styles.buttonLabel}>
                        {startDate ? moment(startDate).format("MM/DD") : ""} 
                        {startDate && endDate ? " - " : ""}
                        {endDate ? moment(endDate).format("MM/DD") : ""}
                    </span>
                )}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    className: styles.menuPaper
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Typography className={styles.menuTitle}>Date Range</Typography>
                
                <div className={styles.datePickerRow}>
                    <div className={styles.inputGroup}>
                        <Typography className={styles.inputLabel}>From</Typography>
                        <DatePicker
                            selected={localStart}
                            onChange={handleStartDateChange}
                            placeholderText="mm/dd/yyyy"
                            className={styles.dateInput}
                            dateFormat="MM/dd/yyyy"
                            popperPlacement="bottom-start"
                            popperClassName={styles.datePickerPopper}
                            onKeyDown={(e) => e.preventDefault()}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <Typography className={styles.inputLabel}>To</Typography>
                        <DatePicker
                            selected={localEnd}
                            onChange={handleEndDateChange}
                            placeholderText="mm/dd/yyyy"
                            className={styles.dateInput}
                            dateFormat="MM/dd/yyyy"
                            minDate={localStart || undefined}
                            popperPlacement="bottom-end"
                            popperClassName={styles.datePickerPopper}
                            onKeyDown={(e) => e.preventDefault()}
                        />
                    </div>
                </div>

                <div className={styles.footer}>
                    <Button 
                        className={styles.clearButton} 
                        onClick={() => { 
                            onClear(); 
                            setLocalStart(null); 
                            setLocalEnd(null); 
                            handleClose(); 
                        }}
                        variant="outlined"
                        size="small"
                    >
                        Clear dates
                    </Button>
                    <Typography className={styles.helperText}>Filters groups (never splits)</Typography>
                </div>
            </Menu>
        </div>
    );
};

export default DateRangeFilter;
